import {
  QUALITY_REPORT_SUFFIXES,
  filesWithoutPartner,
} from "../qualityReports";

const ANALYSIS_INPUT_EXTENSIONS = [".csv", ".xls", ".xlsx"] as const;

const SHARED_FILES_EXTENSIONS = [
  ".pdf",
  ".docx",
  ".txt",
  ".md",
  ".csv",
  ".xls",
  ".xlsx",
  ".pptx",
  ".mp4",
  ".png",
  ".jpg",
  ".jpeg",
  ".zip",
  ".gz",
  ".json",
  ".xml",
  ".yaml",
  ".yml",
] as const;

/**
 * What a folder accepts. IAM enforces the file types (see infra/components/auth.py);
 * the size limit is a client-side guardrail only.
 */
export type UploadRules = {
  readonly allowedExtensions: readonly string[];
  readonly description: string;
  readonly maxSizeBytes: number;
  readonly requiresQualityReports: boolean;
};

/** Analysis inputs: the LiverPRO formats only. */
export const ANALYSIS_INPUT_RULES: UploadRules = {
  allowedExtensions: ANALYSIS_INPUT_EXTENSIONS,
  description: "CSV and Excel (.csv, .xls, .xlsx) files",
  maxSizeBytes: 100 * 1000 * 1000,
  requiresQualityReports: false,
};

export const FOR_APPROVAL_RULES: UploadRules = {
  allowedExtensions: [...ANALYSIS_INPUT_EXTENSIONS, ...QUALITY_REPORT_SUFFIXES],
  description:
    "CSV and Excel results (.csv, .xls, .xlsx) and their data quality reports (<result file name>.data_quality.pdf or .md)",
  maxSizeBytes: 100 * 1000 * 1000,
  requiresQualityReports: true,
};

/** Files Evido shares with a project: reports, guides, images and bundles. */
export const SHARED_FILES_RULES: UploadRules = {
  allowedExtensions: SHARED_FILES_EXTENSIONS,
  description: "documents, images, media and archives",
  maxSizeBytes: 1000 * 1000 * 1000,
  requiresQualityReports: false,
};

/** Dialogues filter on the last extension only. */
export function uploadAcceptAttribute(rules: UploadRules): string {
  const lastExtensions = rules.allowedExtensions.map((extension) =>
    extension.slice(extension.lastIndexOf(".")),
  );
  return [...new Set(lastExtensions)].join(",");
}

/** Case-insensitive; a bare suffix is no file name. */
function matchingSuffix(
  fileName: string,
  allowedExtensions: readonly string[],
): string | undefined {
  const lowerName = fileName.toLowerCase();
  return allowedExtensions.find(
    (allowed) =>
      fileName.length > allowed.length &&
      lowerName.endsWith(allowed.toLowerCase()),
  );
}

export type ExtensionVerdict = "allowed" | "wrongCase" | "unsupported";

/**
 * Why a file's type is or is not acceptable.
 */
export function extensionVerdict(
  fileName: string,
  allowedExtensions: readonly string[],
): ExtensionVerdict {
  const matched = matchingSuffix(fileName, allowedExtensions);
  if (!matched) {
    return "unsupported";
  }
  const suffix = fileName.slice(fileName.length - matched.length);
  return suffix === matched.toLowerCase() || suffix === matched.toUpperCase()
    ? "allowed"
    : "wrongCase";
}

export function hasAllowedExtension(
  fileName: string,
  allowedExtensions: readonly string[],
): boolean {
  return extensionVerdict(fileName, allowedExtensions) === "allowed";
}

export function isWithinSizeLimit(
  sizeInBytes: number,
  maxSizeBytes: number,
): boolean {
  return sizeInBytes <= maxSizeBytes;
}

export function isIndividualFile(file: File): boolean {
  return !file.webkitRelativePath.includes("/");
}

export function isAcceptableUploadFile(
  file: File,
  rules: UploadRules,
): boolean {
  return (
    isIndividualFile(file) &&
    isWithinSizeLimit(file.size, rules.maxSizeBytes) &&
    hasAllowedExtension(file.name, rules.allowedExtensions)
  );
}

export function unpairedFiles(
  droppedFiles: readonly File[],
  rules: UploadRules,
): File[] {
  if (!rules.requiresQualityReports) {
    return [];
  }
  return filesWithoutPartner(
    droppedFiles.filter((file) => isAcceptableUploadFile(file, rules)),
  );
}

export type UploadPolicy = {
  readonly validateFile: (file: File) => boolean;
  /** The rules in force, for the messages that have to describe them. */
  readonly currentRules: () => UploadRules;
  readonly setRules: (rules: UploadRules) => void;
};

export function createUploadPolicy(): UploadPolicy {
  let rules = ANALYSIS_INPUT_RULES;
  return {
    validateFile: (file) => isAcceptableUploadFile(file, rules),
    currentRules: () => rules,
    setRules: (next) => {
      rules = next;
    },
  };
}
